export type MultipleChoiceSelectionState = {
  requiredCount: number | null;
  useExactCount: boolean;
  canVerify: boolean;
  remainingCount: number | null;
  isAtSelectionLimit: boolean;
};

export function resolveMultipleChoiceRequiredCount(
  multipleResponses: boolean,
  correctOptionCount?: number
): number | null {
  if (
    !multipleResponses ||
    typeof correctOptionCount !== "number" ||
    !Number.isFinite(correctOptionCount) ||
    correctOptionCount < 2
  ) {
    return null;
  }
  return correctOptionCount;
}

export function getMultipleChoiceSelectionState(params: {
  multipleResponses: boolean;
  correctOptionCount?: number;
  selectedCount: number;
}): MultipleChoiceSelectionState {
  const requiredCount = resolveMultipleChoiceRequiredCount(
    params.multipleResponses,
    params.correctOptionCount
  );
  const useExactCount = requiredCount !== null;

  return {
    requiredCount,
    useExactCount,
    canVerify: useExactCount
      ? params.selectedCount === requiredCount
      : params.selectedCount > 0,
    remainingCount: useExactCount
      ? Math.max(0, requiredCount - params.selectedCount)
      : null,
    isAtSelectionLimit:
      useExactCount && params.selectedCount >= requiredCount,
  };
}

export type MultipleChoiceStatusLabel = {
  text: string;
  tone: "complete" | "almost" | "remaining";
};

export function getMultipleChoiceStatusLabel(
  state: MultipleChoiceSelectionState
): MultipleChoiceStatusLabel | null {
  if (!state.useExactCount || state.requiredCount == null) {
    return null;
  }

  const remaining = state.remainingCount ?? 0;
  if (remaining === 0) {
    return { text: "Completo", tone: "complete" };
  }
  if (remaining === 1) {
    return { text: "Falta 1", tone: "almost" };
  }
  return { text: `Faltam ${remaining}`, tone: "remaining" };
}

export function getMultipleChoiceSelectedCount(
  state: MultipleChoiceSelectionState
): number {
  if (!state.useExactCount || state.requiredCount == null) {
    return 0;
  }
  return state.requiredCount - (state.remainingCount ?? 0);
}

export function getMultipleChoiceProgressMessage(
  state: MultipleChoiceSelectionState
): string {
  if (!state.useExactCount || state.requiredCount == null) {
    return "Selecione todas as respostas que considerar corretas.";
  }

  const selected = state.requiredCount - (state.remainingCount ?? 0);

  if (state.remainingCount === 0) {
    return `${selected} de ${state.requiredCount} selecionadas.`;
  }

  if (state.remainingCount === 1) {
    return `${selected} de ${state.requiredCount} selecionadas. Falta 1 resposta.`;
  }

  return `${selected} de ${state.requiredCount} selecionadas. Faltam ${state.remainingCount} respostas.`;
}

export function shouldDisableUncheckedMultipleChoiceOption(params: {
  optionId: string;
  selectedOptions: string[];
  state: MultipleChoiceSelectionState;
  isVerifying: boolean;
  readonly: boolean;
  isVerifiedAndCorrect: boolean;
}): boolean {
  if (
    params.isVerifying ||
    params.readonly ||
    params.isVerifiedAndCorrect
  ) {
    return true;
  }

  if (!params.state.isAtSelectionLimit) {
    return false;
  }

  return !params.selectedOptions.includes(params.optionId);
}
